import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Home,
  ChevronRight,
  Send,
  MessageCircle,
  CheckCircle,
  // AlertCircle,
} from "lucide-react";
import Header from "../components/Header/Header";
import ColorInit from "../helpers/ColorInit";
import SEO from "../components/SEO/SEO";

import Footer from "../components/Footer/Footer";
import ScrollToTopButton from "../components/ScrollToTopButton";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<null | "success">(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setSubmitStatus("success");
      setIsSubmitting(false);
      setFormData({ name: "", email: "", subject: "", message: "" });

      // Reset status after 3 seconds
      setTimeout(() => setSubmitStatus(null), 3000);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Address",
      details: [
        "First Branch: Tripoli, El Maarad, Cheikh Nadim El Jisr Street, Near Azhar El Maarad",
        "Second Branch: Tripoli, Riad Soloh Street, Between Benetton and Paind’or",
      ],
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      icon: Phone,
      title: "Phone",
      details: ["+961 81 441 597", "+961 81 33 22 67"],
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      icon: Mail,
      title: "Email",
      details: ["info@blackstore.com"],
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: [
        "Monday - Friday: 9:00 AM - 6:00 PM",
        "Saturday: 10:00 AM - 4:00 PM",
        "Sunday: Closed",
      ],
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
  ];

  return (
    <>
      <SEO
        title="Contact Us"
        description="Visit My Perfume in Tripoli, Lebanon — two branches in El Maarad and Riad Soloh. Reach our team by phone, email, or message form."
      />
      <ScrollToTopButton />
      <ColorInit color={false} />
      <Header />

      {/* Modern Hero Section */}
      <section className="relative bg-linear-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-white/20 backdrop-blur-3xl"></div>
        <div className="absolute top-0 left-0 w-72 h-72 bg-linear-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-linear-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold bg-linear-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-6">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              We'd love to hear from you. Send us a message and we'll respond as
              soon as possible.
            </p>

            {/* Breadcrumb */}
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <Link
                to="/"
                className="flex items-center space-x-1 hover:text-blue-600 transition-colors"
              >
                <Home className="w-4 h-4" />
                <span>Home</span>
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-blue-600 font-medium">Contact</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-linear-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-2xl"></div>

                <div className="relative">
                  <div className="flex items-center space-x-3 mb-8">
                    <div className="w-12 h-12 bg-linear-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      Send us a message
                    </h2>
                  </div>

                  {submitStatus === "success" && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-800 font-medium">
                        Message sent successfully! We'll get back to you soon.
                      </span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="What's this about?"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                        placeholder="Tell us more about how we can help you..."
                        required
                      />
                    </div>

                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full sm:w-auto bg-linear-to-r from-blue-500 to-purple-600 text-white font-semibold py-4 px-8 rounded-xl hover:from-blue-600 hover:to-purple-700 focus:ring-4 focus:ring-blue-200 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            <span>Send Message</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-24 h-24 bg-linear-to-br from-green-400/10 to-blue-400/10 rounded-full blur-2xl"></div>

                <div className="relative">
                  <h3 className="text-2xl font-bold text-gray-900 mb-8">
                    Contact Information
                  </h3>

                  <div className="space-y-6">
                    {contactInfo.map((info, index) => {
                      const IconComponent = info.icon;
                      return (
                        <div key={index} className="group">
                          <div className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                            <div
                              className={`w-12 h-12 ${info.bgColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}
                            >
                              <IconComponent
                                className={`w-6 h-6 ${info.color}`}
                              />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">
                                {info.title}
                              </h4>
                              <div className="space-y-1">
                                {info.details.map((detail, detailIndex) => (
                                  <p
                                    key={detailIndex}
                                    className="text-gray-600 text-sm leading-relaxed"
                                  >
                                    {detail}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Additional Info Card */}
              <div className="bg-linear-to-br from-blue-500 to-purple-600 rounded-2xl shadow-xl p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-xl"></div>

                <div className="relative">
                  <h3 className="text-xl font-bold mb-4">
                    Need immediate help?
                  </h3>
                  <p className="text-blue-100 mb-6 leading-relaxed">
                    For urgent matters, don't hesitate to call us directly. Our
                    support team is ready to assist you.
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Call us now</p>
                      <p className="text-blue-100 text-sm">+961 81 33 22 67</p>
                      <p className="text-blue-100 text-sm">+961 81 441 597</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-linear-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-linear-to-r from-blue-500 to-purple-600 rounded-2xl mb-6">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold bg-linear-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-4">
              Visit Our Store
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Come visit us at our Tripoli location. We're here to help you find
              exactly what you're looking for.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            <div className="relative">
              {/* Map Container with modern styling */}
              <div className="relative h-96 lg:h-125 w-full overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=Gandour%20mosque%2C%20Tripoli%2C%20Lebanon&t=&z=16&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0"
                  title="My Perfume Location - Near Gandour Mosque, Tripoli"
                ></iframe>

                {/* Overlay gradient for better visual integration */}
                <div className="absolute inset-0 bg-linear-to-t from-black/5 via-transparent to-transparent pointer-events-none"></div>
              </div>

              {/* Location Info Overlay */}
              <div className="absolute bottom-6 left-6 right-6 lg:left-8 lg:right-auto lg:max-w-md">
                <div className="bg-white/95 backdrop-blur-lg rounded-2xl p-6 shadow-2xl border border-white/20">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-linear-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">
                        Our Location
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-3">
                        Near Gandour Mosque, Tripoli, El Maarad, Cheikh Nadim El
                        Jisr Street
                      </p>
                      <a
                        href="https://maps.app.goo.gl/8ePhLTcjyXi7Rb5DA"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                      >
                        <span>Get Directions</span>
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Location Details */}
            <div className="p-8 lg:p-12 bg-linear-to-r from-gray-50 to-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">
                    First Branch
                  </h4>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Tripoli, El Maarad, Cheikh Nadim El Jisr Street, Near
                      Azhar El Maarad
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-green-600 shrink-0" />
                    <a
                      href="tel:+96181441597"
                      className="text-gray-600 text-sm hover:text-green-600 transition-colors"
                    >
                      +961 81 441 597
                    </a>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">
                    Second Branch
                  </h4>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Tripoli, Riad Soloh Street, Between Benetton and Paind'or
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-green-600 shrink-0" />
                    <a
                      href="tel:+96181332267"
                      className="text-gray-600 text-sm hover:text-green-600 transition-colors"
                    >
                      +961 81 33 22 67
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ContactPage;
